/*
 * Above the modal layer (1000), not below it.
 *
 * The panel portals to `document.body`, so it competes with `ModalProvider` and
 * `NotificationProvider` rather than nesting inside them. At the old `60` every menu opened from
 * inside a dialog — a `Select` on a form, a `MultiSelect`, a `TagInput` — was painted behind the
 * dialog card: positioned correctly, invisible and unclickable.
 *
 * The trade-off is that a menu opened on the page would paint over a dialog that appeared while it
 * was still open. That ordering is the rarer and the less damaging of the two, and it needs a
 * transient menu to survive the focus move a dialog makes. The lasting fix is a shared layer scale
 * these three providers agree on rather than three hand-picked numbers.
 */
.panel {
  position: fixed;
  z-index: 1010;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 12rem;
  padding: 0.25rem;
  border: 1px solid var(--ring-contrast);
  border-radius: 0.5rem;
  background: var(--surface);
  box-shadow: var(--shadow-lg);
  box-sizing: border-box;
  animation: menu-in 120ms ease-out;
}

@keyframes menu-in {
  from {
    opacity: 0;
    transform: translateY(-0.25rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.list {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  overflow-y: auto;
  overflow-x: hidden;
}
.list:focus {
  outline: none;
}

.header {
  padding: 0.25rem 0.25rem 0.375rem;
}

.footer {
  display: flex;
  gap: 0.5rem;
  padding: 0.375rem 0.25rem 0.25rem;
  border-top: 1px solid var(--border-light);
}

/* Divider between item groups (desktop dropdown + mobile sheet). */
.separator {
  flex-shrink: 0;
  height: 1px;
  margin: 0.25rem 0.5rem;
  background: var(--border-light);
}

/* ── Mobile bottom sheet ── */
/* Same reasoning as `.panel`: below the breakpoint the menu presents as a sheet, and a sheet
   opened from a dialog has to clear the dialog too. */
.sheetRoot {
  position: fixed;
  inset: 0;
  z-index: 1010;
}
.overlay {
  position: absolute;
  inset: 0;
  background: var(--overlay-light);
  animation: sheet-overlay-in 200ms ease both;
}
.overlayClosing {
  animation: sheet-overlay-out 250ms ease both;
}
/* Floats above the bottom edge with a margin on every side; rounded on all corners. */
.sheet {
  position: absolute;
  left: 0.75rem;
  right: 0.75rem;
  bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px));
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  max-height: 80vh;
  padding: 0.5rem;
  border-radius: 1rem;
  background: var(--surface);
  box-shadow: var(--shadow-2xl);
  box-sizing: border-box;
  animation: sheet-in 360ms cubic-bezier(0.16, 1, 0.3, 1) both;
}
.sheetClosing {
  animation: sheet-out 280ms cubic-bezier(0.4, 0, 1, 1) both;
}
/* Fill the sheet and scroll within it. */
.listSheet {
  flex: 1 1 auto;
  min-height: 0;
}

@keyframes sheet-in {
  0% {
    transform: translateY(120%);
  }
  70% {
    transform: translateY(-1.5%);
  }
  100% {
    transform: translateY(0);
  }
}
@keyframes sheet-out {
  0% {
    transform: translateY(0);
  }
  15% {
    transform: translateY(-2%);
  }
  100% {
    transform: translateY(120%);
  }
}
@keyframes sheet-overlay-in {
  from {
    opacity: 0;
  }
}
@keyframes sheet-overlay-out {
  to {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sheet,
  .overlay,
  .overlayClosing,
  .sheetClosing {
    animation: none;
  }
}

/* Windows High Contrast — the shadow/alpha border is dropped, so keep a solid outline. */
@media (forced-colors: active) {
  .panel {
    border: 1px solid CanvasText;
  }
}
